import React, { useState, useEffect } from "react";
import { Card, Steps, Button, Row, Col, Space, Typography, Spin, notification } from "antd";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import TestSetName from "./Steps/TestSetName";
import TestSetRequests from "./Steps/TestSetRequests";
import TestSetReview from "./Steps/TestSetReview";
import { usePermissions } from "../../PermissionsProvider";
import { TestSetService } from "../../services/TestSetService";
import { IntentGroup, TestSet } from "../../model_gen/evaluation";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { IntentService } from "../../services/IntentService";
const { Step } = Steps;
const { Text } = Typography;

interface TestSetData {
  name?: string;
  description?: string;
  requests?: ChatCompletionRequest[];
  is_golden: boolean;
}

export const NEW_TEST_SET_STORAGE_KEY = "testSetData";

const TestSetNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { intentGroupId } = useParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSetData, setTestSetData] = useState<TestSetData>(() => {
    const saved = localStorage.getItem(NEW_TEST_SET_STORAGE_KEY);
    return saved ? JSON.parse(saved) : { 
      requests: [],
      is_golden: false  // Initialize with false
    };
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_TEST_SET_STORAGE_KEY, JSON.stringify(testSetData));
  }, [testSetData]);

  // Update URL when step changes
  useEffect(() => {
    setSearchParams({ step: currentStep.toString() });
  }, [currentStep, setSearchParams]);

  // Fetch application and action data
  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(Number(intentGroupId), false, setLoading, apiKey).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  const steps = [
    {
      title: "Name",
      description: "Set name and description",
      component: (
        <TestSetName
          name={testSetData.name}
          description={testSetData.description}
          onChange={(name: string, description: string) => 
            setTestSetData(prev => ({ ...prev, name, description }))
          }
        />
      ),
    },
    {
      title: "Requests",
      description: "Add test requests",
      component: (
        <TestSetRequests    
          intentGroupId={Number(intentGroupId)}
          onChange={(requests: any[]) => setTestSetData(prev => ({ ...prev, requests }))}
          initialSelectedRequests={testSetData.requests}
        />
      ),
    },
    {
      title: "Review",
      description: "Confirm settings",
      component: (
        <TestSetReview
          testSetData={testSetData}
          apiKey={apiKey}
          onChange={(changes) => setTestSetData(prev => ({ ...prev, ...changes }))}
        />
      ),
    },
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return testSetData.name && testSetData.name.trim().length > 0;
      case 1:
        return testSetData.requests && testSetData.requests.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createTestSet = async () => {
    const payload = {
      name: testSetData.name,
      description: testSetData.description,
      intent_group_id: Number(intentGroupId),
      requests: testSetData.requests.map(request => ({
        request_id: request.request_id,
        metadata: request.metadata || {}
      })),
      is_golden: testSetData.is_golden,
    };
    TestSetService.createTestSet(payload, setLoading, apiKey).subscribe((response: TestSet) => {
        console.log("Test set created successfully");
        localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
        notification.success({
            message: "Test set created successfully",
            description: "Your test set has been created successfully",
        });
        navigate(`/intent-group/${intentGroupId}/testsets/${response.id}`);
    }, (error: any) => {
        console.error("Error creating test set");
        notification.error({
            message: "Error creating test set",
            description: error.message,
        });
    });
  };

  return (
    <Card className="test-sets-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {(!intentGroup) ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link
                    to={"/intent-groups"}
                    style={{ color: "#828282" }}>
                    Intent Groups
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroupId}`}
                    style={{ color: "#828282" }}>
                    {intentGroup.intent_name}
                    {" / "}{" "}
                  </Link>
                  <Link
                    to={`/intent-group/${intentGroupId}/testsets`}
                    style={{ color: "#828282" }}>
                    Test Sets
                    {" / "}{" "}
                  </Link>
                  New
                </>
              )}
            </Typography>
            <Text type="secondary" style={{ display: "block", marginBottom: "24px" }}>
              Step {currentStep + 1} of {steps.length}
            </Text>
          </Col>
        </Row>

        <Steps 
          current={currentStep} 
          progressDot 
          style={{ margin: "12px 0" }}
        >
          {steps.map(step => (
            <Step 
              key={step.title} 
              title={step.title} 
              description={step.description}
            />
          ))}
        </Steps>

        <Card 
          bordered={false} 
          className="step-content"
          style={{ 
            padding: "24px",
            borderRadius: "8px",
            margin: "12px 0"
          }}
        >
          {steps[currentStep].component}
        </Card>

        <Row justify="space-between">
          <Col>
            {currentStep > 0 && (
              <Button 
                size="large"
                onClick={handlePrev}
                disabled={loading}
              >
                Previous Step
              </Button>
            )}
          </Col>
          <Col>
            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={!canProceed() || loading}
              >
                Continue
              </Button>
            ) : (
              <Button 
                type="primary"
                size="large"
                disabled={!canProceed() || loading}
                onClick={createTestSet}
                icon={loading && <Spin size="small" />}
              >
                {loading ? "Creating test set..." : "Create Test Set"}
              </Button>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default TestSetNew;
